/**
 * ECS appointment-write adapter — Phase 5 cutover.
 *
 * Covers two clusters that share this module — both **fully cut over**: their
 * flags (`USE_ECS_APPOINTMENT_WRITES`, `USE_ECS_LINKED_APPOINTMENT_WRITES`) and
 * the legacy AppSync paths behind them were removed once each had been at 100%
 * in prod, so these adapters are now the only implementation.
 *   • Wave 1 — create / schedule / revert-to-inquiry.
 *   • Wave 4 — the linked-appointment cluster: the create-from-existing clones
 *     (supplemental / deposition / re-evaluation; see
 *     `apiCreateFromExistingAppointment`) and the reschedule direct/hold +
 *     confirm/cancel flows (see the reschedule adapters + their module note — a
 *     HYBRID cutover that keeps doc-dup + reassignment on the FE).
 *
 * One legacy AppSync clone survives in `appointment.ts`: a re-evaluation HOLD,
 * which the ECS cloner does not yet accept (REEVAL_HOLD is Phase 3).
 *
 * Described in `specs/appointment-api/phase-5-portal-cutover-plan.md` §1. It maps the
 * Portal's existing `AppointmentFormData` argument shape onto the ECS
 * api-server appointment-operations request DTOs, calls them via the shared
 * `createApiClient()` (Cognito bearer JWT + `API_SERVER_BASE_URL` + correlation
 * id), and maps the `{ data, error }` result back into the same success/throw
 * shape the legacy AppSync methods produced — so the switch lives entirely at
 * the `AppointmentService` method boundary and callers do not change.
 *
 * ── Wave 1 parity gaps (create/schedule) ──
 * (Wave 4's create-from-existing gaps are documented at that function.)
 * Tracked in the parity audit (§3A) + the post-rollout audit
 * `specs/appointment-api/wave1-live-divergences.md`:
 *
 *   • fieldTemplateRef (schedule) — the `/schedule` endpoint doesn't resolve the
 *     doctor's FieldTemplate on INQUIRY→SCHEDULED the way legacy save-details
 *     does; the template-ref sync stream consumer reconciles it off the
 *     SCHEDULED event. See `buildCommonAppointmentFields`.
 *
 * CLOSED: HI-7 (`caseID`) and ME-3 (`isDemo`) — both are now fields on
 * `AppointmentCreatePayload` and are forwarded by `toAppointmentCreatePayload`.
 * CR-2 / W1-06 — all four case fields are declared on both the create and
 * update DTOs (#8188) and both mappers now forward them.
 */

import {
  AppointmentRecord,
  AppointmentStatus,
  ParticipantContactUnavailability,
  ParticipantType,
  ReschedulingReason,
  User,
} from "~/api/API";
import { createApiClient } from "~/api/apiClient";
import type { components } from "~/api/generated/api-server-types";
import { sortPatientCases } from "~/common/sortings";
import { DEFAULT_DATE_ISO, DEFAULT_TIME_ISO } from "~/constants";
import { InternalError, UserError } from "~/error-handling";
import { AppointmentFormData } from "~/pages/appointments/types";
import type { AddressManagementIdsInput } from "./appointment";
import {
  buildContactUnavailabilityInput,
  buildCreatePatientCaseInput,
  sanitizeAdditionalTimeslots,
} from "./helpers/appointment-query-builder";

// ── DTO aliases (generated from the api-server OpenAPI schema) ──────────────
type CreateInquiryRequest = components["schemas"]["CreateInquiryRequest"];
type CreateScheduledRequest = components["schemas"]["CreateScheduledRequest"];
type CreateFromExistingRequest =
  components["schemas"]["CreateFromExistingRequest"];
type CloneType = components["schemas"]["CloneType"];
type RescheduleRequest = components["schemas"]["RescheduleRequest"];
type ConfirmRescheduleRequest =
  components["schemas"]["ConfirmRescheduleRequest"];
type CancelRescheduleRequest = components["schemas"]["CancelRescheduleRequest"];
type StartReschedulingRequest =
  components["schemas"]["StartReschedulingRequest"];
type ScheduleAppointmentRequest =
  components["schemas"]["ScheduleAppointmentRequest"];
type RevertToInquiryRequest = components["schemas"]["RevertToInquiryRequest"];
type AppointmentCreatePayload =
  components["schemas"]["AppointmentCreatePayload"];
type AppointmentUpdatePayload =
  components["schemas"]["AppointmentUpdatePayload"];
type PatientCreatePayload = components["schemas"]["PatientCreatePayload"];
type PatientUpdatePayload = components["schemas"]["PatientUpdatePayload"];
type PatientCaseCreatePayload =
  components["schemas"]["PatientCaseCreatePayload"];
type PatientCaseUpdatePayload =
  components["schemas"]["PatientCaseUpdatePayload"];
type NotePayload = components["schemas"]["NotePayload"];

type NoteContent = AppointmentFormData["notes"]["content"][number];

// ── §1.5 response/error mapping ─────────────────────────────────────────────

/**
 * Translate a non-2xx `createApiClient` result into the same throw shape the
 * legacy AppSync methods produced, so component-level error handling is
 * unaffected. The apiClient `errorMiddleware` already reported the HTTP status
 * to Sentry (with status + body + endpoint), so we must NOT re-report here —
 * doing so defeats the `_isReported` dedup and double-fires the event. We only
 * surface a typed, user-appropriate error.
 *
 *   • 409 ConflictError       → UserError (optimistic-concurrency: someone else
 *                               edited; the user should refresh and retry).
 *   • 422 validation          → InternalError (a payload the Portal built that
 *                               the server rejected — an engineering bug, not a
 *                               user-actionable one; surfaced loudly).
 *   • 500 doc_setup failure   → UserError (the server reverted the row, so a
 *                               retry is safe; its message is for engineers).
 *   • 500 PartialFailureError → InternalError (compound-write failure; the
 *                               server contract reports orphan cleanup in the
 *                               body, already logged by the middleware).
 */
function throwMappedApiError(params: {
  operation: string;
  status: number;
  error: unknown;
}): never {
  const { operation, status, error } = params;

  if (status === 409) {
    throw new UserError(
      "This appointment was just modified by someone else. Refresh the page and try again.",
    );
  }

  // The server already reverted the row, so the user can safely retry; the
  // server's own message ("see server logs") is written for engineers.
  if (status === 500 && isDocSetupFailure(error)) {
    throw new UserError(
      "The appointment could not be scheduled because setting up its documents failed. It is still an inquiry — please try again. If it keeps failing, contact the engineering team.",
    );
  }

  const detail = summarizeApiError(error);
  if (status === 422) {
    throw new InternalError(`${operation} failed validation: ${detail}`);
  }
  throw new InternalError(`${operation} failed (${status}): ${detail}`);
}

function isDocSetupFailure(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const detail = (error as { detail?: unknown }).detail;
  return (
    !!detail &&
    typeof detail === "object" &&
    (detail as { failed_step?: unknown }).failed_step === "doc_setup"
  );
}

/** Best-effort human string from a typed error body, without assuming shape. */
function summarizeApiError(error: unknown): string {
  if (error && typeof error === "object") {
    const detail = (error as { detail?: unknown }).detail;
    if (detail && typeof detail === "object") {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string") return message;
    }
    if (typeof detail === "string") return detail;
    // App-level handlers (INVALID_TRANSITION, RECORD_CONFLICT) put the prose at
    // the top level rather than under `detail`, which FastAPI's own validation
    // errors use. Without this the modal shows raw JSON — e.g. clicking
    // "Create appointment" a second time on an appointment that is already
    // scheduled.
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return JSON.stringify(error);
}

// ── DTO mappers ─────────────────────────────────────────────────────────────
// Each mapper reuses the exact legacy field-derivation helpers so the API
// payload is derived identically to the AppSync path. The generated DTO enum
// fields are string unions carrying the same string values as the `~/api/API`
// enums, so enum-bearing fields are bridged with a narrow `as` at this
// client→server serialization boundary (allowed per CLAUDE.md).

function toPatientCreatePayload(
  patientInfo: AppointmentFormData["patientInfo"]["content"],
): PatientCreatePayload {
  return {
    name: patientInfo.patientName,
    dateOfBirth: patientInfo.dateOfBirth?.toString() || null,
    SSN: patientInfo.SSN || null,
    language:
      (patientInfo.language as PatientCreatePayload["language"]) ?? null,
    dominantHand:
      (patientInfo.dominantHand as PatientCreatePayload["dominantHand"]) ??
      null,
    contact: {
      email: patientInfo.email || null,
      phone: patientInfo.cellPhoneNumber || null,
      homePhoneNumber: patientInfo.homePhoneNumber || null,
      address: {
        street: patientInfo.homeMailingAddress.street || null,
        city: patientInfo.homeMailingAddress.city || null,
        state: patientInfo.homeMailingAddress.state || null,
        zip: patientInfo.homeMailingAddress.zipcode || null,
      },
    },
  };
}

function toPatientUpdatePayload(
  patientID: string,
  patientInfo: AppointmentFormData["patientInfo"]["content"],
): PatientUpdatePayload {
  return {
    ...toPatientCreatePayload(patientInfo),
    id: patientID,
  };
}

/**
 * Reshape a legacy `CreatePatientCaseInput` (built by `buildCreatePatientCaseInput`)
 * into the create DTO by dropping the server-set `patientID`. The CR-2 fields
 * (`scheduledCommsSuppressedParties` + the phone FKs) ride along and are
 * declared on `PatientCaseCreatePayload` since #8188, so the server persists
 * them. `as unknown as` bridges the nominal nested-enum type differences at
 * this serialization boundary.
 */
export function toPatientCaseCreatePayload(
  input: ReturnType<typeof buildCreatePatientCaseInput>,
): PatientCaseCreatePayload {
  const { patientID: _patientID, ...rest } = input;
  return rest as unknown as PatientCaseCreatePayload;
}

/**
 * Reshape a legacy `CreatePatientCaseInput` into the UPDATE DTO for an existing
 * case: drop `patientID` (server-set) and attach the existing case `id`.
 *
 * Everything else forwards, including `scheduledCommsSuppressedParties` and the
 * three managed-phone FKs. This mapper used to strip those four because the
 * `extra="forbid"` update DTO didn't declare them and would have hard-422'd —
 * but #8188 added all four to `PatientCaseUpdatePayload`, so stripping them was
 * pure data loss (omit-don't-clear means the drop surfaced no error at all).
 * `scheduledCommsSuppressedParties` in particular decides which parties the
 * SCHEDULED transition skips in `appointment_sender`, so losing it contacts a
 * party the operator shielded — W1-06.
 */
export function toPatientCaseUpdatePayload(
  id: string,
  input: ReturnType<typeof buildCreatePatientCaseInput>,
): PatientCaseUpdatePayload {
  const { patientID: _patientID, ...rest } = input;
  return { ...rest, id } as unknown as PatientCaseUpdatePayload;
}

/**
 * Build one case's `CreatePatientCaseInput` via the shared legacy builder.
 * `addressManagementIds` (the managed address/phone/fax/lawfirm FKs) is only
 * passed for the primary case, mirroring the legacy create/save paths.
 */
function buildCaseInput(
  caseInfo: AppointmentFormData["claimInfo"]["content"][number],
  formData: AppointmentFormData,
  addressManagementIds?: AddressManagementIdsInput,
  flexibleInjuryDatesEnabled = false,
): ReturnType<typeof buildCreatePatientCaseInput> {
  return buildCreatePatientCaseInput(
    // patientID is server-set on create and stripped on update — pass a
    // placeholder; the mappers above drop it.
    "",
    caseInfo,
    formData.adjusterInfo.content,
    formData.applicantAttorneyInfo.content,
    formData.defenseAttorneyInfo.content,
    formData.nurseCaseManagerInfo.content,
    formData.carbonCopyInfo.content,
    formData.patientInfo.content,
    addressManagementIds,
    flexibleInjuryDatesEnabled,
  );
}

/**
 * Map the shared appointment fields (present on both the create and update
 * payloads). `status`, GSI keys, `s3FileKey`, `primaryPatientCaseID`, and the
 * owner-cognito ids are server-set and intentionally omitted. `isDemo` /
 * `caseID` are create-only, so they are added by `toAppointmentCreatePayload`
 * rather than here.
 *
 * `fieldTemplateRef` is excluded from both DTOs (server-set). On CREATE the
 * server resolves it (`creator._get_field_template_ref`). On SCHEDULE it is a
 * known gap: unlike the legacy `saveAppointmentDetails` — which resolves the
 * doctor's latest FieldTemplate on the INQUIRY→SCHEDULED transition — the
 * `/schedule` endpoint (`scheduler.py`) does NOT set it. The transient miss is
 * self-healed client-side when `PatientInfoTab` next loads. Track before
 * flipping schedule flag-ON.
 *
 * `existingContactUnavailability` carries forward the current row's
 * unavailability map on the UPDATE (schedule) path — the adjuster sub-field has
 * no form control, and the `/schedule` write replaces the whole map rather than
 * deep-merging, so it must be preserved (matches legacy `saveAppointmentDetails`).
 * On CREATE there is no existing row, so it is omitted.
 */
function buildCommonAppointmentFields(
  formData: AppointmentFormData,
  noInterpreterNeeded: boolean,
  existingContactUnavailability?: ParticipantContactUnavailability | null,
): Omit<AppointmentCreatePayload, "inquiryDate"> {
  const appointmentInfo = formData.tentativeAppointment.content;
  const applicantAttorney = formData.applicantAttorneyInfo.content;
  const defenseAttorney = formData.defenseAttorneyInfo.content;

  return {
    date: appointmentInfo.dateTime.date?.toString() || DEFAULT_DATE_ISO,
    time: appointmentInfo.dateTime.time || DEFAULT_TIME_ISO,
    additionalTimeslots: sanitizeAdditionalTimeslots(
      appointmentInfo.additionalTimeslots,
      appointmentInfo.preferredLocation,
    ),
    type:
      (appointmentInfo.appointmentType as AppointmentCreatePayload["type"]) ??
      null,
    doctorID: appointmentInfo.preferredPhysician || null,
    locationID: appointmentInfo.preferredLocation || null,
    durationMinutes: appointmentInfo.durationMinutes,
    appointmentRecordMedicalAssistantId: appointmentInfo.medicalAssistant,
    appointmentRecordSchedulingAssistantId: appointmentInfo.schedulingAssistant,
    appointmentRecordOnSiteOpId: appointmentInfo.onSiteOp,
    // Omitted when empty (the server treats null as unchanged anyway), so an
    // api-server older than these fields — `/schedule` forbids extra keys —
    // only rejects a schedule that actually sets one.
    appointmentRecordReportOpId: appointmentInfo.reportOp || undefined,
    appointmentRecordBillingOpId: appointmentInfo.billingOp || undefined,
    appointmentRecordPreVisitReviewerId:
      appointmentInfo.preVisitReviewer || undefined,
    panelSpecialty:
      (appointmentInfo.panelSpecialty as AppointmentCreatePayload["panelSpecialty"]) ??
      null,
    scheduler: {
      name: appointmentInfo.personToCall,
      organizationName: appointmentInfo.organizationName,
      contact: {
        phone: appointmentInfo.phoneNumber,
        phoneExtension: appointmentInfo.phoneNumberExtension,
        email: appointmentInfo.email || null,
        address: { street: "", city: "", state: "", zip: "" },
        fax: "",
      },
    },
    inquirerInfo: {
      name: formData.inquirerInfo.content.name || null,
      inquiringParty:
        (formData.inquirerInfo.content.party as
          | components["schemas"]["InquiringParty"]
          | null) ?? null,
      contact: {
        email: formData.inquirerInfo.content.email || null,
        phone: formData.inquirerInfo.content.phoneNumber,
        phoneExtension: formData.inquirerInfo.content.phoneNumberExtension,
      },
      notification_110_required:
        formData.inquirerInfo.content.notification_110_required,
    },
    interpreter:
      noInterpreterNeeded === false
        ? {
            companyName: appointmentInfo.interpreterCompanyName,
            phoneNumber: appointmentInfo.interpreterPhoneNumber,
            phoneExtension: appointmentInfo.interpreterPhoneNumberExtension,
            note: appointmentInfo.interpreterNote,
            name: appointmentInfo.interpreterName,
            certificateId: appointmentInfo.interpreterCertificateID,
          }
        : {},
    defenseAttorneyNotPresent: Boolean(defenseAttorney.isNotPresent),
    applicantAttorneyNotPresent: Boolean(applicantAttorney.isNotPresent),
    contactUnavailability: buildContactUnavailabilityInput(
      formData,
      existingContactUnavailability,
    ) as AppointmentCreatePayload["contactUnavailability"],
  };
}

/**
 * Create-only appointment fields on top of the shared ones.
 *
 * `caseID` links the new appointment into an existing `CaseRecord` chain when
 * the create was started from a case page (`?caseId=`). `isDemo` is sent only
 * when true — mirroring legacy `isDemo || undefined`, so `false` is never
 * stored explicitly.
 */
function toAppointmentCreatePayload(
  formData: AppointmentFormData,
  noInterpreterNeeded: boolean,
  isDemo: boolean,
  caseID: string | null | undefined,
): AppointmentCreatePayload {
  return {
    ...buildCommonAppointmentFields(formData, noInterpreterNeeded),
    inquiryDate:
      formData.inquirerInfo.content.date?.toString() || DEFAULT_DATE_ISO,
    caseID: caseID ?? null,
    isDemo: isDemo || null,
  };
}

export function toNotePayloads(
  notes: NoteContent[],
  user: User | null,
): NotePayload[] {
  if (!user) return [];
  const payloads: NotePayload[] = [];
  for (const note of notes) {
    const timeStamp = note.dateTime.getTime();
    if (Number.isNaN(timeStamp)) continue;
    payloads.push({
      content: note.content,
      notesAuthorId: user.id,
      timeStamp,
      type: "GENERAL_MA_NOTES",
    });
  }
  return payloads;
}

// ── Public adapters (called from the AppointmentService switch) ─────────────

export interface ApiCreateAppointmentArgs {
  formData: AppointmentFormData;
  user: User | null;
  noInterpreterNeeded: boolean;
  targetAppointmentStatus?: AppointmentStatus;
  addressManagementIds?: AddressManagementIdsInput;
  isDemo?: boolean;
  caseID?: string | null;
  /**
   * Intake files staged in this session (create-scheduled only). The server's
   * post-commit doc setup copies them into the appointment's S3 prefix, unioned
   * with any `tempUploadedIntakeFormS3Keys` already on the row — so the Portal
   * must NOT also run the doc lambda on this path (W1-05 / W1-08).
   */
  intakeFormS3Keys?: string[];
  flexibleInjuryDatesEnabled?: boolean;
}

/**
 * Flag-ON create path. Routes INQUIRY → `POST /appointments/create-inquiry`
 * and SCHEDULED → `POST /appointments/create-scheduled`. Returns the new
 * appointment id (matching the legacy `createAppointmentRecord` string return);
 * throws a mapped error on failure.
 */
export async function apiCreateAppointmentRecord(
  args: ApiCreateAppointmentArgs,
): Promise<string> {
  const {
    formData,
    user,
    noInterpreterNeeded,
    targetAppointmentStatus,
    addressManagementIds,
    isDemo = false,
    caseID,
    intakeFormS3Keys,
    flexibleInjuryDatesEnabled = false,
  } = args;

  const isScheduled = targetAppointmentStatus === AppointmentStatus.SCHEDULED;

  // Mirror the legacy guard: create-scheduled generates the per-appointment
  // s3FileKey from the first claim number + appointment date, so both must be
  // present before we call the server.
  if (isScheduled) {
    const firstClaimNumber = formData.claimInfo.content[0]?.claimNumber;
    const appointmentDate =
      formData.tentativeAppointment.content.dateTime.date?.toString();
    if (!firstClaimNumber || !appointmentDate) {
      throw new UserError(
        "First claim number or serialized appointment date is missing",
      );
    }
  }

  const patient = toPatientCreatePayload(formData.patientInfo.content);
  const patient_cases = formData.claimInfo.content.map((caseInfo, index) =>
    toPatientCaseCreatePayload(
      // Managed FKs only apply to the primary (first) case, matching legacy.
      buildCaseInput(
        caseInfo,
        formData,
        index === 0 ? addressManagementIds : undefined,
        flexibleInjuryDatesEnabled,
      ),
    ),
  );
  const appointment = toAppointmentCreatePayload(
    formData,
    noInterpreterNeeded,
    isDemo,
    caseID,
  );
  const notes = toNotePayloads(formData.notes.content, user);

  const client = createApiClient();

  if (isScheduled) {
    const body: CreateScheduledRequest = {
      appointment,
      patient,
      patient_cases,
      notes,
      intake_form_s3_keys: intakeFormS3Keys?.length ? intakeFormS3Keys : null,
    };
    const { data, error, response } = await client.POST(
      "/appointments/create-scheduled",
      { body },
    );
    if (error || !data) {
      throwMappedApiError({
        operation: "Create scheduled appointment",
        status: response.status,
        error: error ?? "empty response body",
      });
    }
    return data.appointment_id;
  }

  const body: CreateInquiryRequest = {
    appointment,
    patient,
    patient_cases,
    notes,
  };
  const { data, error, response } = await client.POST(
    "/appointments/create-inquiry",
    { body },
  );
  if (error || !data) {
    throwMappedApiError({
      operation: "Create inquiry appointment",
      status: response.status,
      error: error ?? "empty response body",
    });
  }
  return data.appointment_id;
}

export interface ApiScheduleAppointmentArgs {
  appointment: AppointmentRecord;
  formData: AppointmentFormData;
  user: User | null;
  noInterpreterNeeded: boolean;
  addressManagementIds?: AddressManagementIdsInput;
  /**
   * Intake files staged in this session. The server's post-commit doc setup
   * unions them with the row's `tempUploadedIntakeFormS3Keys` and copies each
   * once — the Portal must NOT also run the doc lambda (W1-05 / W1-08).
   */
  intakeFormS3Keys?: string[];
  flexibleInjuryDatesEnabled?: boolean;
}

/**
 * Flag-ON schedule path (INQUIRY → SCHEDULED) via `POST /{id}/schedule`.
 * Direct-DDB on the backend, so it is already write-attributed (no §2 wait).
 *
 * The case payloads carry the full CR-2 field set — `scheduledCommsSuppressedParties`
 * plus the managed-phone FKs — since #8188 declared them on the update DTO and
 * W1-06 removed the adapter-side strip.
 */
export async function apiScheduleAppointmentFromInquiry(
  args: ApiScheduleAppointmentArgs,
): Promise<string[]> {
  const {
    appointment,
    formData,
    user,
    noInterpreterNeeded,
    addressManagementIds,
    intakeFormS3Keys,
    flexibleInjuryDatesEnabled = false,
  } = args;

  const firstClaimNumber = formData.claimInfo.content[0]?.claimNumber;
  const appointmentDate =
    formData.tentativeAppointment.content.dateTime.date?.toString();
  if (!firstClaimNumber || !appointmentDate) {
    throw new UserError(
      "First claim number or serialized appointment date is missing",
    );
  }

  const sortedPatientCases = sortPatientCases(appointment);
  const primaryPatientCase = sortedPatientCases[0]?.patientCase;
  if (!primaryPatientCase) {
    throw new InternalError("No valid primary case found");
  }
  const patientID = primaryPatientCase.patientID;
  if (!patientID) {
    throw new InternalError("Patient ID is missing");
  }

  // Update-payload appointment fields. Scheduling commits one slot, so the
  // proposed alternates are cleared (the server also clears them; the explicit
  // [] matches the legacy withClearedAdditionalTimeslots intent).
  const commonFields = buildCommonAppointmentFields(
    formData,
    noInterpreterNeeded,
    // Carry the adjuster's contact-unavailability forward — it has no form
    // control and the /schedule write replaces the whole map (matches legacy
    // saveAppointmentDetails).
    appointment.contactUnavailability ?? null,
  );
  const appointmentPayload: AppointmentUpdatePayload = {
    ...commonFields,
    additionalTimeslots: [],
    inquiryDate:
      formData.inquirerInfo.content.date?.toString() || DEFAULT_DATE_ISO,
    videoCallLink: formData.tentativeAppointment.content.videoCallLink || null,
  };

  const patient = toPatientUpdatePayload(
    patientID,
    formData.patientInfo.content,
  );

  // Pair each form claim to the appointment's existing case by sort order
  // (primary + others), mirroring saveAppointmentDetails. New/removed cases at
  // schedule time are CR-4 (case reconcile) territory — out of this slice; only
  // existing cases are updated here.
  const otherCases = sortedPatientCases.slice(1);
  const patient_cases: PatientCaseUpdatePayload[] = [];
  formData.claimInfo.content.forEach((caseInfo, index) => {
    const existingCaseId =
      index === 0
        ? primaryPatientCase.id
        : otherCases[index - 1]?.patientCase?.id;
    if (!existingCaseId) return; // no existing case row to update → skip (CR-4)
    patient_cases.push(
      toPatientCaseUpdatePayload(
        existingCaseId,
        buildCaseInput(
          caseInfo,
          formData,
          index === 0 ? addressManagementIds : undefined,
          flexibleInjuryDatesEnabled,
        ),
      ),
    );
  });

  const body: ScheduleAppointmentRequest = {
    appointment: appointmentPayload,
    patient,
    patient_cases,
    updated_at: appointment.updatedAt,
    intake_form_s3_keys: intakeFormS3Keys?.length ? intakeFormS3Keys : null,
    // The form's whole note list — freshly-typed ones merged with the existing
    // ones by `handleSubmit`. The server creates only what the row doesn't
    // already carry (deduped on timeStamp + content), so re-sending unchanged
    // notes is a no-op and brand-new ones stop vanishing (W1-07).
    notes: toNotePayloads(formData.notes.content, user),
  };

  const client = createApiClient();
  const { data, error, response } = await client.POST(
    "/appointments/{appointment_id}/schedule",
    { params: { path: { appointment_id: appointment.id } }, body },
  );
  if (error || !data) {
    throwMappedApiError({
      operation: "Schedule appointment",
      status: response.status,
      error: error ?? "empty response body",
    });
  }
  // Staged intake files whose upload expired before scheduling (temp-pdfs/ is
  // swept after 30 days). Scheduling still succeeded; the caller tells the user.
  return data.skipped_intake_files ?? [];
}

export interface ApiRevertToInquiryArgs {
  appointment: AppointmentRecord;
}

/**
 * Flag-ON revert-to-inquiry path (SCHEDULED → INQUIRY) via
 * `POST /{id}/revert-to-inquiry`. Direct-DDB on the backend, so it is already
 * write-attributed (no §2 wait) and carries NO parity gaps — the endpoint sets
 * only `status` and keeps the slot (date/time/doctor), field-for-field matching
 * the legacy `handleOpenRevertToInquiryModal` inline mutation.
 *
 * The only body field is the `updated_at` optimistic-concurrency precondition
 * (stale → 409 → mapped to a UserError). The legacy inline mutation discards its
 * return value, so this resolves `void`; errors reuse the shared
 * `throwMappedApiError` mapping (no double Sentry report).
 */
export async function apiRevertToInquiry(
  args: ApiRevertToInquiryArgs,
): Promise<void> {
  const { appointment } = args;

  const body: RevertToInquiryRequest = { updated_at: appointment.updatedAt };

  const client = createApiClient();
  const { data, error, response } = await client.POST(
    "/appointments/{appointment_id}/revert-to-inquiry",
    { params: { path: { appointment_id: appointment.id } }, body },
  );
  if (error || !data) {
    throwMappedApiError({
      operation: "Revert appointment to inquiry",
      status: response.status,
      error: error ?? "empty response body",
    });
  }
}

export interface ApiRestoreArgs {
  appointmentId: string;
}

/** Where the post-restore inherited-MA re-check left the appointment. */
export type RestoredMedicalAssistant =
  components["schemas"]["RestoredMedicalAssistantOutcome"];

/**
 * Restore a cancelled appointment (CANCELLED / LATE_CANCELLED → SCHEDULED, or
 * DEPOSITION_HOLD for a hold that kept its alternate slots) via
 * `POST /{id}/restore`. Clears the `cancellation*` fields, leaving
 * `cancellationRequestedInternally` set — the same field set the Portal wrote
 * directly before this endpoint existed.
 *
 * The target status is resolved server-side from the row's type + retained
 * timeslots, so it is deliberately not a parameter: deriving it here from a
 * record the browser may have been holding for minutes is exactly the race the
 * endpoint's version pin closes.
 *
 * Also what makes a restore re-check its medical assistant — the endpoint runs
 * the shared inherited-MA re-validation, which a direct AppSync write bypasses
 * — and returns where that landed, so the caller can tell the operator when
 * the restore handed the appointment to someone else.
 *
 * Empty body for the same reason as `apiRestoreInquiry`: `RestoreRequest.
 * updated_at` is deprecated and ignored server-side.
 *
 * Resolves the MA outcome, or null when the server did not report one (an
 * api-server older than this Portal build).
 *
 * Errors reuse the shared `throwMappedApiError` mapping.
 */
export async function apiRestore(
  args: ApiRestoreArgs,
): Promise<RestoredMedicalAssistant | null> {
  const { appointmentId } = args;

  const client = createApiClient();
  const { data, error, response } = await client.POST(
    "/appointments/{appointment_id}/restore",
    { params: { path: { appointment_id: appointmentId } }, body: {} },
  );
  if (error || !data) {
    throwMappedApiError({
      operation: "Restore appointment",
      status: response.status,
      error: error ?? "empty response body",
    });
  }
  // Null, not undefined-by-accident: the Portal and the api-server deploy
  // independently, so a Portal that ships first talks to a server whose
  // restore response has no `medical_assistant` yet. "The server didn't say"
  // is a real answer here and the caller has to handle it either way.
  return data.medical_assistant ?? null;
}

export interface ApiRestoreInquiryArgs {
  appointmentId: string;
}

/**
 * Reopen a closed inquiry (INQUIRY_CLOSED → INQUIRY) via
 * `POST /{id}/restore-inquiry`. Clears the `inquiryClosing*` fields.
 *
 * The body is empty on purpose: `RestoreInquiryRequest.updated_at` is
 * deprecated and ignored server-side — the endpoint guards on the row's source
 * status, not on a client-supplied version — so sending it would imply an
 * optimistic-concurrency check that isn't there.
 *
 * Resolves `void`; errors reuse the shared `throwMappedApiError` mapping.
 */
export async function apiRestoreInquiry(
  args: ApiRestoreInquiryArgs,
): Promise<void> {
  const { appointmentId } = args;

  const client = createApiClient();
  const { data, error, response } = await client.POST(
    "/appointments/{appointment_id}/restore-inquiry",
    { params: { path: { appointment_id: appointmentId } }, body: {} },
  );
  if (error || !data) {
    throwMappedApiError({
      operation: "Restore inquiry",
      status: response.status,
      error: error ?? "empty response body",
    });
  }
}

export interface ApiCreateFromExistingArgs {
  cloneType: CloneType;
  sourceAppointmentId: string;
  /** New committed slot date (ISO 8601). */
  date: string;
  /**
   * New slot time. Required for deposition / re-evaluation; optional for
   * supplemental (the server defaults it). Left null → server default.
   */
  time?: string | null;
  /**
   * Override the copied duration. The server keeps the source's when null —
   * so the deposition caller MUST pass its resolved doctor-default here (ME-2),
   * else the clone inherits the source (e.g. QME) duration.
   */
  durationMinutes?: number | null;
  /** Override the inquiry date; server defaults to today when null. */
  inquiryDate?: string | null;
  /** Override the copied location id; server keeps the source's when null. */
  locationId?: string | null;
  /** Re-evaluations: drop the copied interpreter for re-confirmation. */
  shouldRemoveInterpreter?: boolean;
}

/**
 * Flag-ON linked-appointment clone path — the create-from-existing cluster
 * (Wave 4). Routes supplemental / deposition / re-evaluation clones through
 * `POST /appointments/create-from-existing`. The endpoint reads the ENTIRE
 * clone from the source row server-side (new patient + cases with the same
 * claim numbers, scheduler / doctor / panel / general notes, data-field values,
 * CR-2 case fields, HI-1 employments/referredBy/billingInfo, ME-3 isDemo),
 * commits atomically, links the source, and runs post-commit doc-setup — so the
 * request carries only what the caller picks for the new visit. Returns the new
 * appointment id (matching the legacy `create*FromExistingAppointment` string
 * return); throws a mapped error on failure.
 *
 * ── Carried divergences on the API path (flag stays OFF until acceptable) ────
 * These are backend behaviors the endpoint owns; the FE can't close them here,
 * so they're documented at the switch sites in `appointment.ts`:
 *
 *   • scheduling-assistant + on-site-op come from the SOURCE row. The legacy
 *     clones set the NEW scheduling assistant to the acting user
 *     (`user.assistant.id`) and re-resolve the on-site op via `autoAssignData`.
 *     The endpoint copies the source's — a divergence for both fields.
 *   • Re-eval INQUIRER OVERRIDES (party / email / phone / extension) have no DTO
 *     field — only `inquiry_date` is carried. The modal's overrides are dropped
 *     on the API path (source inquirer info is copied verbatim otherwise).
 *   • Re-eval NON-ADL data-field values + supplemental BILLING MODIFIER
 *     inheritance land with PR #8194 (open). Until it merges, the endpoint drops
 *     ALL re-eval data-field values and carries diagnosis-mappings only.
 *   • Deposition `panelSpecialty` is over-copied by the endpoint (PR #8194
 *     drops it) — cosmetic, not data loss.
 */
export async function apiCreateFromExistingAppointment(
  args: ApiCreateFromExistingArgs,
): Promise<string> {
  const body: CreateFromExistingRequest = {
    clone_type: args.cloneType,
    source_appointment_id: args.sourceAppointmentId,
    date: args.date,
    time: args.time ?? null,
    duration_minutes: args.durationMinutes ?? null,
    inquiry_date: args.inquiryDate ?? null,
    location_id: args.locationId ?? null,
    should_remove_interpreter: args.shouldRemoveInterpreter ?? true,
  };

  const client = createApiClient();
  const { data, error, response } = await client.POST(
    "/appointments/create-from-existing",
    { body },
  );
  if (error || !data) {
    throwMappedApiError({
      operation: `Create ${args.cloneType} from existing appointment`,
      status: response.status,
      error: error ?? "empty response body",
    });
  }
  return data.appointment_id;
}

// ── Reschedule cluster (Wave 4, HYBRID flip) ────────────────────────────────
//
// The `/reschedule` + `/confirm-reschedule` + `/cancel-reschedule` endpoints own
// the compound two-record write server-side: the atomic clone + source
// transition (direct → RESCHEDULED/LATE_RESCHEDULED/NO_SHOW/CANCELLED; hold →
// RESCHEDULING_IN_PROGRESS), full field carry-over (scheduler/inquirer/
// interpreter/panel/billing/data-fields/jotform/documentStatus/employments/
// referredBy/pages-declarations…), best-effort insights copy, the history-call
// carry-over/clear plan, the bidirectional link, and source-fail-cancels-clone.
//
// THREE behaviors the endpoints deliberately DON'T do (deferred), so the caller
// keeps them on the FE after the API returns — this is why the switch is a
// HYBRID, not a clean switch-and-return:
//   • Google-Doc duplication — the `run_doc_duplication` hook is None on both
//     `/reschedule` and `/confirm-reschedule`. The clone/confirmed row is created
//     doc-less; the FE runs `duplicateAndInheritGoogleDocs` afterwards.
//   • MA / on-site-op reassignment — the endpoint copies both from the source;
//     the FE re-resolves them (auto-assign, skipped within 14 days) and patches
//     the clone. `scheduling_assistant_id` IS a request field, so it's carried
//     here (no post-step needed for it).
//   • Inherited AppointmentFileReference junction rows — the endpoint writes none
//     (it relies on read-time chain inheritance, gated on
//     PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT, which isn't on in prod). The FE
//     keeps writing them (`createInheritedFileReferences`) so inherited docs stay
//     visible to junction-scoped consumers regardless of the read-flag state.

export interface ApiRescheduleArgs {
  sourceAppointmentId: string;
  reason: ReschedulingReason;
  /** Free-text reason; only valid (server-side) when `reason` is `OTHERS`. */
  othersReason?: string | null;
  date: string;
  time: string;
  locationId?: string | null;
  isHold: boolean;
  isLate: boolean;
  requestedBy?: ParticipantType | null;
  requestedInternally: boolean;
  note?: string;
  requesterDisplayName?: string | null;
  schedulingAssistantId?: string | null;
  shouldRemoveInterpreter?: boolean;
  additionalTimeslots?: Array<{
    date: string;
    time: string;
    locationID?: string | null;
  }>;
}

/**
 * Flag-ON direct + hold reschedule via `POST /appointments/reschedule`. Returns
 * the new clone's id (matching the legacy `create*RescheduledFromExisting*`
 * string return). `is_hold` selects the sub-flow; `additional_timeslots` is only
 * meaningful for a hold. The caller must still run the FE doc-duplication +
 * staffing-reassignment post-steps (see the module note above).
 */
export async function apiRescheduleFromExisting(
  args: ApiRescheduleArgs,
): Promise<string> {
  const body: RescheduleRequest = {
    source_appointment_id: args.sourceAppointmentId,
    reason: args.reason as RescheduleRequest["reason"],
    others_reason: args.othersReason ?? null,
    date: args.date,
    time: args.time,
    location_id: args.locationId ?? null,
    is_hold: args.isHold,
    is_late: args.isLate,
    requested_by:
      (args.requestedBy as RescheduleRequest["requested_by"]) ?? null,
    requested_internally: args.requestedInternally,
    note: args.note ?? "",
    requester_display_name: args.requesterDisplayName ?? null,
    scheduling_assistant_id: args.schedulingAssistantId ?? null,
    should_remove_interpreter: args.shouldRemoveInterpreter ?? true,
    additional_timeslots: (args.additionalTimeslots ?? []).map((slot) => ({
      date: slot.date,
      time: slot.time,
      location_id: slot.locationID ?? null,
    })),
  };

  const client = createApiClient();
  const { data, error, response } = await client.POST(
    "/appointments/reschedule",
    { body },
  );
  if (error || !data) {
    throwMappedApiError({
      operation: `Reschedule appointment${args.isHold ? " (hold)" : ""}`,
      status: response.status,
      error: error ?? "empty response body",
    });
  }
  return data.appointment_id;
}

/**
 * The confirm / cancel endpoints resolve the hold pair server-side from the
 * addressed record and return both ids: `appointmentId` is the active scheduled
 * one after the op (confirm → the now-scheduled hold; cancel → the restored
 * source), `originalAppointmentId` is its counterpart.
 */
export interface ApiReschedulePairResult {
  appointmentId: string;
  originalAppointmentId: string;
}

export interface ApiConfirmRescheduleArgs {
  /** Either side of the hold pair — the server resolves the counterpart. */
  addressedAppointmentId: string;
  date: string;
  time: string;
  locationId?: string | null;
  /** Set only when changed — older api-servers 422 on the extra key. */
  durationMinutes?: number;
  /** Optimistic-concurrency precondition for the addressed record (stale → 409). */
  updatedAt: string;
}

/**
 * Flag-ON confirm-reschedule via `POST /{id}/confirm-reschedule`. Resolves the
 * hold pair, commits the hold's confirmed slot, transitions the source, carries
 * the history call, and flips the hold to SCHEDULED — all server-side. The
 * caller still duplicates the source's Google Docs onto the confirmed row.
 */
export async function apiConfirmReschedule(
  args: ApiConfirmRescheduleArgs,
): Promise<ApiReschedulePairResult> {
  const body: ConfirmRescheduleRequest = {
    date: args.date,
    time: args.time,
    location_id: args.locationId ?? null,
    updated_at: args.updatedAt,
    ...(args.durationMinutes !== undefined
      ? { duration_minutes: args.durationMinutes }
      : {}),
  };

  const client = createApiClient();
  const { data, error, response } = await client.POST(
    "/appointments/{appointment_id}/confirm-reschedule",
    { params: { path: { appointment_id: args.addressedAppointmentId } }, body },
  );
  if (error || !data) {
    throwMappedApiError({
      operation: "Confirm reschedule hold",
      status: response.status,
      error: error ?? "empty response body",
    });
  }
  return {
    appointmentId: data.appointment_id,
    originalAppointmentId: data.original_appointment_id,
  };
}

export interface ApiCancelRescheduleArgs {
  /** Either side of the hold pair — the server resolves the counterpart. */
  addressedAppointmentId: string;
  /** Optimistic-concurrency precondition for the addressed record (stale → 409). */
  updatedAt: string;
}

/**
 * Flag-ON cancel-reschedule via `POST /{id}/cancel-reschedule`. Discards the
 * hold and restores the source (from either side), clearing the rescheduling
 * fields. No doc/post-step follow-up (cancel duplicates nothing).
 */
export async function apiCancelReschedule(
  args: ApiCancelRescheduleArgs,
): Promise<ApiReschedulePairResult> {
  const body: CancelRescheduleRequest = { updated_at: args.updatedAt };

  const client = createApiClient();
  const { data, error, response } = await client.POST(
    "/appointments/{appointment_id}/cancel-reschedule",
    { params: { path: { appointment_id: args.addressedAppointmentId } }, body },
  );
  if (error || !data) {
    throwMappedApiError({
      operation: "Cancel reschedule hold",
      status: response.status,
      error: error ?? "empty response body",
    });
  }
  return {
    appointmentId: data.appointment_id,
    originalAppointmentId: data.original_appointment_id,
  };
}

// ── Hold-less rescheduling (CR-6) ───────────────────────────────────────────
//
// The hold-LESS siblings of the reschedule cluster: move an appointment into
// RESCHEDULING_IN_PROGRESS without minting a hold clone, and the inverse reset.
//   • `apiStartRescheduling` → `POST /{id}/start-rescheduling` (new endpoint) —
//     replaces `markReschedulingInProgress`. Single-row: sets the rescheduling
//     fields + keeps the slot. Carries the HI-2 `others_reason` set-point.
//   • the hold-less CANCEL reuses `apiCancelReschedule` above — the same
//     `/cancel-reschedule` endpoint resolves the hold-less source server-side and
//     returns an empty `originalAppointmentId` (no hold), so no separate adapter.
// Both are plain single-row transitions, so — unlike the two-record reschedule
// clone flip — there are NO FE post-steps (no doc-dup / reassignment / file-refs).

export interface ApiStartReschedulingArgs {
  appointmentId: string;
  reason: ReschedulingReason;
  /** Free-text reason; only valid (server-side) when `reason` is `OTHER`. */
  othersReason?: string | null;
  note?: string;
  requestedBy?: ParticipantType | null;
  requestedInternally: boolean;
  /** Optimistic-concurrency precondition for the addressed record (stale → 409). */
  updatedAt: string;
}

/**
 * Flag-ON hold-less "mark rescheduling in progress" via
 * `POST /{id}/start-rescheduling`. Direct-DDB single-row transition, so it is
 * already write-attributed (no §2 wait). The legacy method returns void (the
 * mutation result is discarded), so this resolves `void`.
 */
export async function apiStartRescheduling(
  args: ApiStartReschedulingArgs,
): Promise<void> {
  const body: StartReschedulingRequest = {
    updated_at: args.updatedAt,
    reason: args.reason as StartReschedulingRequest["reason"],
    others_reason: args.othersReason ?? null,
    note: args.note ?? "",
    requested_by:
      (args.requestedBy as StartReschedulingRequest["requested_by"]) ?? null,
    requested_internally: args.requestedInternally,
  };

  const client = createApiClient();
  const { data, error, response } = await client.POST(
    "/appointments/{appointment_id}/start-rescheduling",
    { params: { path: { appointment_id: args.appointmentId } }, body },
  );
  if (error || !data) {
    throwMappedApiError({
      operation: "Start rescheduling",
      status: response.status,
      error: error ?? "empty response body",
    });
  }
}
